import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useSearchParams } from "react-router-dom";

import i18n from "../../locales/i18n";
import {
  getProgramScopedLocaleConfig,
  getProgramScopedLocaleOptions,
  getProgramScopedLocaleStorageKey,
  hasValidProgramScopedLocaleUrlValue,
  isProgramScopedLocaleSupported,
  normalizeProgramScopedLocale,
  PROGRAM_SCOPED_LOCALE_QUERY_PARAM,
  resolveProgramScopedLocale
} from "./programScopedLocaleConfig";

function readLocalStorageValue(storageKey) {
  if (!storageKey || typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(storageKey);
  } catch (err) {
    return null;
  }
}

function writeLocalStorageValue(storageKey, locale) {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, locale);
  } catch (err) {
    // Scoped Program locale persistence is helpful but not required for
    // execution. Ignore storage failures so rendering remains safe.
  }
}

function removeLocalStorageValue(storageKey) {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch (err) {
    // Ignore storage failures for the same reason writes are best-effort.
  }
}

export function resolveScopedTranslation(t, key, ...fallbacks) {
  if (key) {
    const translated = t(key, { defaultValue: "" });

    if (typeof translated === "string" && translated.trim()) {
      return translated;
    }
  }

  for (const fallback of fallbacks) {
    if (typeof fallback === "string" && fallback.trim()) {
      return fallback;
    }
  }

  return key || "";
}

const defaultLocaleContext = Object.freeze({
  isEnabled: false,
  config: null,
  activeLocale: "en",
  defaultLocale: "en",
  supportedLocales: Object.freeze(["en"]),
  supportedLocaleOptions: Object.freeze([]),
  programT: i18n.getFixedT("en"),
  documentLocale: "en",
  contentLang: "en",
  setLocale: () => false,
  translate: (key, ...fallbacks) =>
    resolveScopedTranslation(i18n.getFixedT("en"), key, ...fallbacks),
  buildSearchWithLocale: () => ""
});

const ProgramScopedLocaleContext = createContext(defaultLocaleContext);

export function ProgramScopedLocaleProvider({
  projectSlug,
  programSlug,
  userCommunicationLanguage,
  children
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setStorageVersion] = useState(0);

  const config = useMemo(
    () => getProgramScopedLocaleConfig({ projectSlug, programSlug }),
    [projectSlug, programSlug]
  );

  const storageKey = useMemo(
    () => getProgramScopedLocaleStorageKey(config),
    [config]
  );

  const urlLocale = searchParams.get(PROGRAM_SCOPED_LOCALE_QUERY_PARAM);
  const storedLocale = readLocalStorageValue(storageKey);

  const activeLocale = useMemo(
    () => resolveProgramScopedLocale({
      config,
      urlLocale,
      storedLocale,
      userCommunicationLanguage
    }),
    [
      config,
      urlLocale,
      storedLocale,
      userCommunicationLanguage
    ]
  );

  const supportedLocaleOptions = useMemo(
    () => getProgramScopedLocaleOptions(config),
    [config]
  );

  const programT = useMemo(
    () => i18n.getFixedT(activeLocale),
    [activeLocale]
  );

  useEffect(() => {
    if (!config || !storageKey) {
      return;
    }

    if (
      urlLocale &&
      !hasValidProgramScopedLocaleUrlValue(config, urlLocale)
    ) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(PROGRAM_SCOPED_LOCALE_QUERY_PARAM);
      setSearchParams(nextSearchParams, { replace: true });
      return;
    }

    if (
      storedLocale &&
      !isProgramScopedLocaleSupported(config, storedLocale)
    ) {
      removeLocalStorageValue(storageKey);
      setStorageVersion((current) => current + 1);
      return;
    }

    if (!hasValidProgramScopedLocaleUrlValue(config, urlLocale)) {
      return;
    }

    const normalizedUrlLocale = normalizeProgramScopedLocale(urlLocale);

    if (normalizedUrlLocale && storedLocale !== normalizedUrlLocale) {
      writeLocalStorageValue(storageKey, normalizedUrlLocale);
      setStorageVersion((current) => current + 1);
    }
  }, [
    config,
    searchParams,
    setSearchParams,
    storageKey,
    storedLocale,
    urlLocale
  ]);

  const setLocale = useCallback(
    (nextLocale) => {
      if (!config || !storageKey) {
        return false;
      }

      const normalizedNextLocale =
        normalizeProgramScopedLocale(nextLocale);

      if (!isProgramScopedLocaleSupported(config, normalizedNextLocale)) {
        return false;
      }

      writeLocalStorageValue(storageKey, normalizedNextLocale);
      setStorageVersion((current) => current + 1);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set(
        PROGRAM_SCOPED_LOCALE_QUERY_PARAM,
        normalizedNextLocale
      );
      setSearchParams(nextSearchParams, { replace: true });

      return true;
    },
    [config, searchParams, setSearchParams, storageKey]
  );

  const buildSearchWithLocale = useCallback(
    (updates = {}) => {
      const nextSearchParams = new URLSearchParams(searchParams);

      if (config) {
        nextSearchParams.set(PROGRAM_SCOPED_LOCALE_QUERY_PARAM, activeLocale);
      }

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          nextSearchParams.delete(key);
        } else {
          nextSearchParams.set(key, String(value));
        }
      });

      const queryString = nextSearchParams.toString();

      return queryString ? `?${queryString}` : "";
    },
    [activeLocale, config, searchParams]
  );

  const translate = useCallback(
    (key, ...fallbacks) =>
      resolveScopedTranslation(programT, key, ...fallbacks),
    [programT]
  );

  const contextValue = useMemo(
    () => {
      if (!config) {
        return {
          ...defaultLocaleContext,
          programT,
          translate,
          buildSearchWithLocale
        };
      }

      return {
        isEnabled: true,
        config,
        activeLocale,
        defaultLocale: config.defaultLocale,
        supportedLocales: config.supportedLocales,
        supportedLocaleOptions,
        programT,
        documentLocale: activeLocale,
        contentLang: activeLocale,
        setLocale,
        translate,
        buildSearchWithLocale
      };
    },
    [
      activeLocale,
      buildSearchWithLocale,
      config,
      programT,
      setLocale,
      supportedLocaleOptions,
      translate
    ]
  );

  return (
    <ProgramScopedLocaleContext.Provider value={contextValue}>
      {children}
    </ProgramScopedLocaleContext.Provider>
  );
}

export function useProgramScopedLocale() {
  return useContext(ProgramScopedLocaleContext);
}
